import { useState, type MouseEvent } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import { useAtomValue } from 'jotai'
import LinkIcon from '@mui/icons-material/Link'
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded'
import Alert from '@mui/material/Alert'
import Grid from '@mui/material/Unstable_Grid2'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup'
import { styled } from '@mui/material/styles'
import Paper from '@mui/material/Paper'

import DetailHeader from '@/components/collection-point/detail/detail-header'
import CollectionPointDetailBins from '@/components/collection-point/detail/waste-containers'
import CollectionPointAdmin from '@/components/collection-point/detail/admin'
import Payers from '@/components/collection-point/detail/payers'
import { useCollectionPoint } from '@/hooks/api/use-collection-point'
import LoadingBox from '@/components/loading-box'
import ErrorBox from '@/components/error-box'
import { selectedCollectionPointIdAtom, selectedMunicipalityIdAtom } from '@/state'
import HeadingWithIcon from '@/components/heading-with-icon'
import MixedWeightDiscountTarget from './mixed-weight-discount-target'
import MixedVolumeDiscountTarget from './mixed-volume-discount-target'
import ForcedDiscountTarget from './forced-discount-target'
import CreateAttributesChangeRequestForm from '@/components/forms/create-attributes-change-request'
import CollectionPointPublicLink from './public-link'
import BioWeightDiscountTarget from './bio-weight-discount-target'
import { YEARS_FROM_START_TO_THIS } from '@/constants/general'

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
    [`& .${toggleButtonGroupClasses.grouped}`]: {
        margin: '3px',
        border: 0,
        borderRadius: 10,
        [`&.${toggleButtonGroupClasses.disabled}`]: {
            border: 0,
        },
    },
    [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
        marginLeft: -1,
        borderLeft: '1px solid transparent',
    },
}))

export default function CollectionPointDetailWrapper() {
    
    const collectionPointId = useAtomValue(selectedCollectionPointIdAtom)
    const municipalityId = useAtomValue(selectedMunicipalityIdAtom)

    const [change, setChange] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>(false)

    const toggleSuccess = () => {
        setSuccess(!success)
    }

    const [selectedYear, setSelectedYear] = useState<number>(YEARS_FROM_START_TO_THIS[0])

    const handleChangeYear = (_event: MouseEvent<HTMLElement>, newValue: number) => {
        setSelectedYear(newValue)
    }
    
    const { status, data, error } = useCollectionPoint(collectionPointId)

    if (status === 'pending') {
        return (
            <LoadingBox />
        )
    }

    if (error) {
        return <ErrorBox
            error={error}
            message={`Nepovedlo se načíst detail svozového místa s ID ${collectionPointId}`}
        />
    }

    const ext = data.exts.find(ext => ext.year === selectedYear)

    const inMotivationProgram = ext?.in_motivation_program
    const forcedDiscountCents = ext?.force_discount_cents
    const mixedVolumeCategoriesDisabled = ext?.active_mixed_volume_category?.motivation_model?.mixed_volume_categories_disabled
    const mixedWeightCategoriesDisabled = ext?.active_mixed_weight_category?.motivation_model?.mixed_weight_categories_disabled
    const bioWeightCategoriesDisabled = ext?.active_bio_weight_category?.motivation_model?.bio_weight_categories_disabled
    
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
        }}>
            <Box  sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>
                <Typography variant="h2" component="h1" mb={2.5}>
                    Detail svozového místa: {data.code}
                </Typography>
                <DetailHeader collectionPointDetail={data} />
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}>
                <HeadingWithIcon
                    text="Slevový cíl"
                    icon={<SavingsRoundedIcon />}
                />
                <Box sx={{
                    display: 'flex',
                }}>
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            borderRadius: 3,
                            backgroundColor: 'customGrey.boxBackgroundLight',
                        }}
                    >
                        <StyledToggleButtonGroup
                            color="primary"
                            size="small"
                            value={selectedYear}
                            exclusive
                            onChange={handleChangeYear}
                            aria-label="group list view"
                        >
                            {YEARS_FROM_START_TO_THIS.map(year => (
                                <ToggleButton key={year} value={year} aria-label="grouped" sx={{ py: 0.5, px: 1.5 }}>
                                    <Typography variant="h6">
                                        {year}
                                    </Typography>
                                </ToggleButton>
                            ))}
                        </StyledToggleButtonGroup>
                    </Paper>
                </Box>
                {(!inMotivationProgram && forcedDiscountCents == null) ?
                    <Alert
                        severity="info"
                    >
                        Svozové místo se v tomto roce neúčastní motivačního programu.
                    </Alert>
                    :
                    <Box sx={{
                        display: 'flex',
                        flexDirection: {
                            xs: 'column',
                            md: 'row',
                        },
                        flexWrap: 'wrap',
                        columnGap: 3,
                        rowGap: 2,
                    }}>
                        {forcedDiscountCents != null ? 
                            <ForcedDiscountTarget amountCents={forcedDiscountCents} />
                            :
                            <>
                                {!mixedWeightCategoriesDisabled &&
                                    <MixedWeightDiscountTarget
                                        collectionPointId={data.id}
                                        year={selectedYear}
                                    />
                                }
                                {!mixedVolumeCategoriesDisabled &&
                                    <MixedVolumeDiscountTarget
                                        collectionPointId={data.id}
                                        year={selectedYear}
                                    />
                                }
                                {!bioWeightCategoriesDisabled &&
                                    <BioWeightDiscountTarget
                                        collectionPointId={data.id}
                                        year={selectedYear}
                                    />
                                }
                            </>
                        }
                    </Box>
                }
            </Box>
            <CollectionPointDetailBins bins={data.bins} />
            <Grid container rowSpacing={4} columnSpacing={6}>
                <Grid xs={12} sm={6}>
                    <CollectionPointAdmin admin={data.admin}/>
                </Grid>
                <Grid xs={12} sm={6}>
                    <Payers
                        householdMembers={data.household_members}
                        numberOfMembers={data.number_of_members}
                    />
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 3,
                    }}>
                        <HeadingWithIcon
                            text="Přístup pro ostatní poplatníky"
                            icon={<LinkIcon />}
                        />
                        <CollectionPointPublicLink />
                    </Box>
                </Grid>
            </Grid>
            <Container sx={{
                display: 'flex',
                marginTop: '1rem',
                justifyContent: 'center',
            }} maxWidth="sm">
                {change ?
                    <Box flexGrow={1} minHeight={300}>
                        {success ? 
                            <Alert
                                severity="success"
                                sx={(theme) => ({
                                    flexGrow: 1,
                                    margin: theme.spacing(1, 0),
                                })}
                                action={
                                    <Button
                                        color="success"
                                        size="small"
                                        onClick={() => {
                                            toggleSuccess()
                                            setChange(false)
                                        }}
                                    >
                                        Zavřít
                                    </Button>
                                }
                            >
                                Žádost o změnu úspěšně odeslána.
                            </Alert>
                            :
                            <CreateAttributesChangeRequestForm
                                municipalityId={municipalityId}
                                toggleSuccess={toggleSuccess}
                                closeCallback={() => setChange(false)}
                            />
                        }
                    </Box>
                    :
                    <Button
                        variant="contained"
                        color="terciary"
                        onClick={() => setChange(true)}
                    >
                        Nahlásit změnu
                    </Button>
                }
            </Container>
        </Box>
    )
}
